import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  Plus,
  Pin,
  Trash2,
  Lock,
  MessageSquare,
  FileText,
  Brain,
  Layers,
  MoreHorizontal,
  Pencil,
  Download,
  X,
  Check,
  File,
  Link2,
  AtSign,
} from 'lucide-react';
import { useWorkspace } from '@/hooks/useWorkspace';
import { cn } from '@/lib/utils';
import type { ContextItem, MemoryItem, Project } from '@/lib/workspace/types';
import { estimateContextChars, DEFAULT_CONTEXT_BUDGET } from '@/lib/workspace/prompt';
import {
  ProjectNostrContextCard,
  ProjectPlainContextCard,
} from '@/components/workspace/ProjectNostrContextCard';

// ─── Shared panel shell ───

function PanelSection({
  title,
  action,
  children,
  className,
}: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('border-b border-[#1f1f1f] last:border-b-0', className)}>
      <div className="flex items-center justify-between gap-2 px-4 py-3">
        <h3 className="text-[13px] font-medium text-[#e8e6e3]">{title}</h3>
        {action}
      </div>
      <div className="px-4 pb-4">{children}</div>
    </div>
  );
}

// ─── Instructions editor (expand → auto-save) ───

function InstructionsPanel({ project }: { project: Project }) {
  const { updateProject } = useWorkspace();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(project.instructions);
  const [savedFlash, setSavedFlash] = useState(false);
  const dirtyRef = useRef(false);
  const flashTimer = useRef<number | undefined>(undefined);

  // Sync in external changes (sync pull, import) when not mid-edit
  useEffect(() => {
    if (!dirtyRef.current) setValue(project.instructions);
  }, [project.id, project.instructions]);

  // Instant-apply: debounce writes while typing
  useEffect(() => {
    if (!dirtyRef.current) return;
    const t = window.setTimeout(() => {
      void updateProject(project.id, { instructions: value }).then(() => {
        dirtyRef.current = false;
        setSavedFlash(true);
        window.clearTimeout(flashTimer.current);
        flashTimer.current = window.setTimeout(() => setSavedFlash(false), 1600);
      });
    }, 700);
    return () => window.clearTimeout(t);
  }, [value, project.id, updateProject]);

  useEffect(() => () => window.clearTimeout(flashTimer.current), []);

  const flush = () => {
    if (!dirtyRef.current) return;
    dirtyRef.current = false;
    void updateProject(project.id, { instructions: value });
  };

  const hasContent = !!project.instructions?.trim();
  const preview = project.instructions?.trim() || '';

  return (
    <PanelSection
      title="Instructions"
      action={
        <span className="flex items-center gap-1.5">
          {savedFlash && (
            <span className="inline-flex items-center gap-1 text-[10px] text-green-400/80">
              <Check className="w-3 h-3" />
              Saved
            </span>
          )}
          <button
            type="button"
            onClick={() => {
              if (open) flush();
              setOpen((o) => !o);
            }}
            className="p-1 rounded-md text-[#5a5a5a] hover:text-[#e8e6e3] hover:bg-[#1a1a1a] transition-colors"
            title={open ? 'Done' : 'Edit instructions'}
          >
            {open ? <X className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
          </button>
        </span>
      }
    >
      {open ? (
        <div className="space-y-2.5">
          <p className="text-[11px] text-[#5a5a5a] leading-relaxed">
            Project-only rules stacked after your{' '}
            <span className="text-[#8a8884]">global instructions</span> (Settings). Saves as
            you type.
          </p>
          <textarea
            value={value}
            onChange={(e) => {
              dirtyRef.current = true;
              setValue(e.target.value);
            }}
            onBlur={flush}
            rows={8}
            autoFocus
            placeholder="e.g. Prefer TypeScript. Cite sources. Use our brand voice…"
            className="w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl px-3 py-2.5 text-[13px] text-[#e8e6e3] placeholder:text-[#3a3a3a] focus:outline-none focus:border-[#3a3a3a] resize-y min-h-[140px] leading-relaxed"
          />
        </div>
      ) : hasContent ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="w-full text-left rounded-xl border border-[#1f1f1f] bg-[#0f0f0f] px-3 py-2.5 hover:border-[#2a2a2a] transition-colors"
        >
          <p className="text-[12px] text-[#8a8884] line-clamp-4 leading-relaxed whitespace-pre-wrap">
            {preview}
          </p>
        </button>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="w-full text-left text-[12px] text-[#5a5a5a] hover:text-[#8a8884] transition-colors leading-relaxed"
        >
          Add project instructions (on top of global)
        </button>
      )}
    </PanelSection>
  );
}

// ─── Memory panel (instant inline edit, like context cards) ───

function MemoryPanel({ project }: { project: Project }) {
  const { addMemoryItem, updateMemoryItem, removeMemoryItem } = useWorkspace();
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');

  const items = project.memoryItems ?? [];

  const handleAdd = async () => {
    const content = draft.trim();
    if (!content) return;
    await addMemoryItem(project.id, { content, source: 'manual' });
    setDraft('');
    setAdding(false);
  };

  return (
    <PanelSection
      title="Memory"
      action={
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border border-[#2a2a2a] text-[10px] text-[#5a5a5a]">
            <Lock className="w-2.5 h-2.5" />
            Only you
          </span>
          <button
            type="button"
            onClick={() => {
              setAdding((a) => !a);
              setDraft('');
            }}
            className="p-1 rounded-md text-[#5a5a5a] hover:text-[#e8e6e3] hover:bg-[#1a1a1a] transition-colors"
            title="Add memory"
          >
            {adding ? <X className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
          </button>
        </div>
      }
    >
      {items.length === 0 && !adding && (
        <p className="text-[12px] text-[#5a5a5a] leading-relaxed">
          Project memory will show here. Add facts the model should remember across chats.
        </p>
      )}

      <div className="space-y-2">
        {items.map((item: MemoryItem) => (
          <div
            key={item.id}
            className="group rounded-xl border border-[#1f1f1f] bg-[#0f0f0f] px-3 py-2.5 hover:border-[#2a2a2a] transition-colors"
          >
            <div className="flex items-start gap-2">
              <Brain className="w-3.5 h-3.5 text-[#3a3a3a] shrink-0 mt-0.5" />
              <textarea
                value={item.content}
                onChange={(e) =>
                  void updateMemoryItem(project.id, item.id, {
                    content: e.target.value,
                  })
                }
                rows={2}
                placeholder="A fact to remember…"
                className="flex-1 min-w-0 bg-transparent text-[12px] text-[#8a8884] placeholder:text-[#3a3a3a] focus:text-[#c8c6c3] focus:outline-none resize-y leading-relaxed"
              />
              <button
                type="button"
                onClick={() => void removeMemoryItem(project.id, item.id)}
                className="p-1 rounded text-[#3a3a3a] hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                title="Remove"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          </div>
        ))}

        {adding && (
          <div className="space-y-2 rounded-xl border border-[#2a2a2a] bg-[#0a0a0a] p-3">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={3}
              autoFocus
              placeholder="A fact to remember, e.g. “Prefer TypeScript strict mode”"
              className="w-full bg-transparent text-[12px] text-[#e8e6e3] placeholder:text-[#3a3a3a] focus:outline-none resize-y leading-relaxed"
            />
            <div className="flex justify-end gap-1.5">
              <button
                type="button"
                onClick={() => {
                  setAdding(false);
                  setDraft('');
                }}
                className="px-2 py-1 text-[11px] text-[#5a5a5a] hover:text-[#e8e6e3]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void handleAdd()}
                disabled={!draft.trim()}
                className="px-2.5 py-1 text-[11px] font-medium bg-[#e8e6e3] text-[#0a0a0a] rounded-md disabled:opacity-30"
              >
                Add
              </button>
            </div>
          </div>
        )}
      </div>
    </PanelSection>
  );
}

// ─── Context budget meter ───

function BudgetMeter({ project }: { project: Project }) {
  const { settings } = useWorkspace();
  const stats = estimateContextChars(project, settings);
  const budget = DEFAULT_CONTEXT_BUDGET;
  const pct = Math.min(100, Math.round((stats.rawChars / budget) * 100));
  const over = stats.rawChars > budget;

  return (
    <div className="mt-3 space-y-1.5">
      <div className="flex items-center justify-between text-[10px]">
        <span className="text-[#5a5a5a]">
          {stats.items} item{stats.items === 1 ? '' : 's'}
        </span>
        <span
          className={cn('tabular-nums', over ? 'text-amber-500/80' : 'text-[#3a3a3a]')}
          title="Approximate size injected per chat"
        >
          ~{stats.rawChars.toLocaleString()} / {budget.toLocaleString()} chars
        </span>
      </div>
      <div className="h-1 rounded-full bg-[#1a1a1a] overflow-hidden">
        <div
          className={cn(
            'h-full rounded-full transition-all duration-300',
            over ? 'bg-amber-500/70' : pct >= 75 ? 'bg-amber-500/45' : 'bg-[#4a4a4a]',
          )}
          style={{ width: `${Math.max(2, pct)}%` }}
        />
      </div>
      <p
        className={cn(
          'text-[10px] leading-snug',
          over ? 'text-amber-500/70' : 'text-[#3a3a3a]',
        )}
      >
        {over
          ? 'Over budget — lowest-priority items are skipped. Pin what must always ship.'
          : 'Pinned items ship first when the budget runs out.'}
      </p>
    </div>
  );
}

// ─── Context panel ───

function ContextPanel({
  project,
  onOpenNostrPicker,
}: {
  project: Project;
  onOpenNostrPicker?: () => void;
}) {
  const { addContextItem, updateContextItem, removeContextItem } = useWorkspace();
  const [adding, setAdding] = useState(false);
  const [newType, setNewType] = useState<ContextItem['type']>('note');
  const [newTitle, setNewTitle] = useState('');
  const [newContent, setNewContent] = useState('');

  const items = project.contextItems ?? [];

  const handleAdd = async () => {
    if (!newContent.trim()) return;
    await addContextItem(project.id, {
      type: newType,
      title: newTitle.trim() || undefined,
      content: newContent.trim(),
      meta:
        newType === 'nostr'
          ? {
              ref: newContent.trim(),
              entity: 'event',
              pack: 'single',
            }
          : undefined,
    });
    setAdding(false);
    setNewTitle('');
    setNewContent('');
    setNewType('note');
  };

  const typeIcon = (type: ContextItem['type']) => {
    if (type === 'url') return <Link2 className="w-3.5 h-3.5" />;
    if (type === 'nostr') return <AtSign className="w-3.5 h-3.5" />;
    return <File className="w-3.5 h-3.5" />;
  };

  return (
    <PanelSection
      title="Context"
      action={
        <div className="flex items-center gap-0.5">
          {onOpenNostrPicker && (
            <button
              type="button"
              onClick={onOpenNostrPicker}
              className="p-1 rounded-md text-[#5a5a5a] hover:text-[#e8e6e3] hover:bg-[#1a1a1a] transition-colors"
              title="Add Nostr"
            >
              <AtSign className="w-3.5 h-3.5" />
            </button>
          )}
          <button
            type="button"
            onClick={() => setAdding((a) => !a)}
            className="p-1 rounded-md text-[#5a5a5a] hover:text-[#e8e6e3] hover:bg-[#1a1a1a] transition-colors"
            title="Add context"
          >
            {adding ? <X className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
          </button>
        </div>
      }
    >
      {items.length === 0 && !adding && (
        <div className="space-y-2">
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="w-full rounded-xl border border-dashed border-[#2a2a2a] bg-[#0c0c0c] px-4 py-6 flex flex-col items-center gap-3 hover:border-[#3a3a3a] transition-colors group"
          >
            <div className="relative flex items-center justify-center">
              <div className="absolute -left-5 w-8 h-10 rounded-md border border-[#2a2a2a] bg-[#141414] rotate-[-8deg] opacity-60" />
              <div className="absolute -right-5 w-8 h-10 rounded-md border border-[#2a2a2a] bg-[#141414] rotate-[8deg] opacity-60" />
              <div className="relative w-10 h-12 rounded-md border border-[#2a2a2a] bg-[#1a1a1a] flex items-center justify-center">
                <FileText className="w-4 h-4 text-[#3a3a3a]" />
              </div>
            </div>
            <p className="text-[12px] text-[#5a5a5a] text-center leading-relaxed max-w-[200px] group-hover:text-[#8a8884] transition-colors">
              Add notes, URLs, or Nostr refs to reference in this project.
            </p>
          </button>
          {onOpenNostrPicker && (
            <button
              type="button"
              onClick={onOpenNostrPicker}
              className="w-full flex items-center justify-center gap-2 rounded-xl border border-[#2a2a2a] bg-[#141414] px-3 py-2.5 text-[12px] text-[#e8e6e3] hover:border-[#3a3a3a] transition-colors"
            >
              <AtSign className="w-3.5 h-3.5 text-[#5a5a5a]" />
              Add Nostr… (profile, notes, paste)
            </button>
          )}
        </div>
      )}

      <div className="space-y-2">
        {items.map((item) =>
          item.type === 'nostr' ? (
            <ProjectNostrContextCard
              key={item.id}
              item={item}
              onUpdate={(patch) => void updateContextItem(project.id, item.id, patch)}
              onRemove={() => void removeContextItem(project.id, item.id)}
              onTogglePin={() =>
                void updateContextItem(project.id, item.id, { pinned: !item.pinned })
              }
            />
          ) : (
            <ProjectPlainContextCard
              key={item.id}
              item={item}
              typeIcon={typeIcon(item.type)}
              onUpdate={(patch) => void updateContextItem(project.id, item.id, patch)}
              onRemove={() => void removeContextItem(project.id, item.id)}
              onTogglePin={() =>
                void updateContextItem(project.id, item.id, { pinned: !item.pinned })
              }
            />
          ),
        )}

        {adding && (
          <div className="rounded-xl border border-[#2a2a2a] bg-[#0a0a0a] p-3 space-y-2.5">
            <div className="flex gap-1 flex-wrap">
              {(['note', 'url', 'nostr'] as const).map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => {
                    if (t === 'nostr' && onOpenNostrPicker) {
                      onOpenNostrPicker();
                      setAdding(false);
                      return;
                    }
                    setNewType(t);
                  }}
                  className={cn(
                    'px-2.5 py-1 text-[11px] rounded-lg transition-colors',
                    newType === t
                      ? 'bg-[#e8e6e3] text-[#0a0a0a]'
                      : 'text-[#5a5a5a] hover:text-[#e8e6e3]',
                  )}
                >
                  {t}
                </button>
              ))}
            </div>
            <input
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              placeholder="Title (optional)"
              className="w-full bg-[#111] border border-[#2a2a2a] rounded-lg px-3 py-2 text-[13px] text-[#e8e6e3] placeholder:text-[#3a3a3a] focus:outline-none"
            />
            <textarea
              value={newContent}
              onChange={(e) => setNewContent(e.target.value)}
              placeholder={
                newType === 'url'
                  ? 'https://…'
                  : newType === 'nostr'
                    ? 'npub1… / nevent1… / note1…'
                    : 'Paste notes, specs, snippets…'
              }
              rows={4}
              autoFocus
              className="w-full bg-[#111] border border-[#2a2a2a] rounded-lg px-3 py-2 text-[13px] text-[#e8e6e3] placeholder:text-[#3a3a3a] focus:outline-none resize-y font-mono"
            />
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => void handleAdd()}
                disabled={!newContent.trim()}
                className="flex-1 bg-[#e8e6e3] text-[#0a0a0a] text-[12px] font-medium py-2 rounded-xl disabled:opacity-30"
              >
                Add item
              </button>
              <button
                type="button"
                onClick={() => setAdding(false)}
                className="px-3 text-[12px] text-[#5a5a5a] hover:text-[#e8e6e3]"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {items.length > 0 && <BudgetMeter project={project} />}
    </PanelSection>
  );
}

// ─── Main hub ───

export interface ProjectHubViewProps {
  project: Project;
  /** Composer (new chat in this project) */
  composer: ReactNode;
  onOpenChat: (chatId: string) => void;
  onRenameProject?: () => void;
  onExportProject?: () => void;
  onDeleteProject?: () => void;
  /** Open shared Nostr picker to add project context */
  onOpenNostrPicker?: () => void;
}

export function ProjectHubView({
  project,
  composer,
  onOpenChat,
  onRenameProject,
  onExportProject,
  onDeleteProject,
  onOpenNostrPicker,
}: ProjectHubViewProps) {
  const { chats, deleteChat, updateChat, createChat } = useWorkspace();
  const [tab, setTab] = useState<'chats' | 'activity'>('chats');
  const [menuOpen, setMenuOpen] = useState(false);
  const [renamingChatId, setRenamingChatId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const menuRef = useRef<HTMLDivElement>(null);

  const projectChats = useMemo(
    () =>
      chats
        .filter((c) => c.projectId === project.id)
        .sort((a, b) => b.updatedAt - a.updatedAt),
    [chats, project.id],
  );

  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [menuOpen]);

  const createdLabel = useMemo(() => {
    try {
      return new Date(project.createdAt).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return '';
    }
  }, [project.createdAt]);

  const commitChatRename = async () => {
    if (!renamingChatId) return;
    const title = renameValue.trim();
    if (title) await updateChat(renamingChatId, { title });
    setRenamingChatId(null);
  };

  return (
    <div className="flex-1 min-h-0 overflow-y-auto scrollbar-thin">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-8">
          <div className="min-w-0">
            <h1
              className="text-2xl sm:text-[1.85rem] font-medium text-[#e8e6e3] tracking-tight truncate"
              style={{ letterSpacing: '-0.03em' }}
            >
              {project.name}
            </h1>
            <p className="mt-1.5 text-[12px] text-[#5a5a5a] flex flex-wrap items-center gap-x-2 gap-y-1">
              <span>Created {createdLabel}</span>
              <span className="text-[#2a2a2a]">·</span>
              <span className="inline-flex items-center gap-1">
                <Lock className="w-3 h-3" />
                Private to you
              </span>
            </p>
            {project.description?.trim() && (
              <p className="mt-2 text-[13px] text-[#6a6864] max-w-xl leading-relaxed">
                {project.description}
              </p>
            )}
          </div>

          <div className="relative shrink-0" ref={menuRef}>
            <button
              type="button"
              onClick={() => setMenuOpen((o) => !o)}
              className="p-2 rounded-xl text-[#5a5a5a] hover:text-[#e8e6e3] hover:bg-[#1a1a1a] transition-colors"
              aria-label="Project menu"
            >
              <MoreHorizontal className="w-4 h-4" />
            </button>
            {menuOpen && (
              <div className="absolute right-0 top-full mt-1 z-50 w-44 bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl shadow-xl py-1 od-enter">
                {onRenameProject && (
                  <button
                    type="button"
                    onClick={() => {
                      setMenuOpen(false);
                      onRenameProject();
                    }}
                    className="w-full text-left px-3 py-2 text-[12px] text-[#8a8884] hover:text-[#e8e6e3] hover:bg-[#222] flex items-center gap-2"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                    Rename
                  </button>
                )}
                {onExportProject && (
                  <button
                    type="button"
                    onClick={() => {
                      setMenuOpen(false);
                      onExportProject();
                    }}
                    className="w-full text-left px-3 py-2 text-[12px] text-[#8a8884] hover:text-[#e8e6e3] hover:bg-[#222] flex items-center gap-2"
                  >
                    <Download className="w-3.5 h-3.5" />
                    Export
                  </button>
                )}
                {onDeleteProject && (
                  <button
                    type="button"
                    onClick={() => {
                      setMenuOpen(false);
                      onDeleteProject();
                    }}
                    className="w-full text-left px-3 py-2 text-[12px] text-red-400/70 hover:text-red-400 hover:bg-[#222] flex items-center gap-2 border-t border-[#242424] mt-0.5"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Delete
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Main grid: composer + chats | side panels */}
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_300px] xl:grid-cols-[minmax(0,1fr)_320px] gap-6 lg:gap-8 items-start">
          <div className="min-w-0 space-y-6">
            {/* New chat composer */}
            <div>{composer}</div>

            {/* Chats / Activity tabs */}
            <div>
              <div className="flex items-center justify-between gap-3 mb-3">
                <div className="flex items-center gap-0.5 p-0.5 rounded-xl bg-[#121212] border border-[#1f1f1f]">
                  <button
                    type="button"
                    onClick={() => setTab('chats')}
                    className={cn(
                      'px-3.5 py-1.5 text-[12px] rounded-[10px] transition-colors',
                      tab === 'chats'
                        ? 'bg-[#1f1f1f] text-[#e8e6e3] font-medium shadow-sm'
                        : 'text-[#5a5a5a] hover:text-[#c8c6c3]',
                    )}
                  >
                    Chats
                    {projectChats.length > 0 && (
                      <span className="ml-1.5 text-[10px] text-[#3a3a3a] tabular-nums">
                        {projectChats.length}
                      </span>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => setTab('activity')}
                    className={cn(
                      'px-3.5 py-1.5 text-[12px] rounded-[10px] transition-colors',
                      tab === 'activity'
                        ? 'bg-[#1f1f1f] text-[#e8e6e3] font-medium shadow-sm'
                        : 'text-[#5a5a5a] hover:text-[#c8c6c3]',
                    )}
                  >
                    Activity
                  </button>
                </div>
                <p className="text-[11px] text-[#3a3a3a] hidden sm:inline-flex items-center gap-1">
                  <Lock className="w-3 h-3" />
                  Private
                </p>
              </div>

              {tab === 'chats' && (
                <div className="space-y-0.5">
                  {projectChats.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
                      <div className="w-10 h-10 rounded-full bg-[#141414] border border-[#1f1f1f] flex items-center justify-center mb-4">
                        <MessageSquare className="w-4 h-4 text-[#3a3a3a]" />
                      </div>
                      <p className="text-[13px] text-[#5a5a5a] max-w-sm leading-relaxed">
                        Dork references the same knowledge every time you talk to it in this
                        project.
                      </p>
                      <button
                        type="button"
                        onClick={() => void createChat(project.id)}
                        className="mt-5 text-[12px] text-[#8a8884] hover:text-[#e8e6e3] transition-colors"
                      >
                        Start a chat below ↑
                      </button>
                    </div>
                  ) : (
                    projectChats.map((chat) => (
                      <div
                        key={chat.id}
                        className="group flex items-center gap-2 px-3 py-2.5 rounded-xl hover:bg-[#141414] transition-colors"
                      >
                        <MessageSquare className="w-3.5 h-3.5 text-[#3a3a3a] shrink-0" />
                        {renamingChatId === chat.id ? (
                          <input
                            autoFocus
                            value={renameValue}
                            onChange={(e) => setRenameValue(e.target.value)}
                            onBlur={() => void commitChatRename()}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') void commitChatRename();
                              if (e.key === 'Escape') setRenamingChatId(null);
                            }}
                            className="flex-1 min-w-0 bg-[#111] border border-[#2a2a2a] rounded-md px-2 py-1 text-[13px] text-[#e8e6e3] focus:outline-none"
                          />
                        ) : (
                          <button
                            type="button"
                            onClick={() => onOpenChat(chat.id)}
                            className="flex-1 min-w-0 text-left text-[13px] text-[#c8c6c3] hover:text-[#e8e6e3] truncate"
                          >
                            {chat.title || 'New chat'}
                          </button>
                        )}
                        <span className="text-[11px] text-[#3a3a3a] shrink-0 tabular-nums hidden sm:inline">
                          {formatRelative(chat.updatedAt)}
                        </span>
                        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                          <button
                            type="button"
                            onClick={() => {
                              setRenamingChatId(chat.id);
                              setRenameValue(chat.title || '');
                            }}
                            className="p-1 rounded text-[#3a3a3a] hover:text-[#e8e6e3]"
                            title="Rename"
                          >
                            <Pencil className="w-3 h-3" />
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              if (confirm('Delete this chat?')) void deleteChat(chat.id);
                            }}
                            className="p-1 rounded text-[#3a3a3a] hover:text-red-400"
                            title="Delete"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {tab === 'activity' && (
                <div className="py-10 px-4 text-center">
                  <Layers className="w-5 h-5 text-[#3a3a3a] mx-auto mb-3" />
                  <p className="text-[13px] text-[#5a5a5a] leading-relaxed max-w-sm mx-auto">
                    {projectChats.length === 0
                      ? 'Activity will appear as you chat in this project.'
                      : `${projectChats.length} chat${projectChats.length === 1 ? '' : 's'} · last updated ${formatRelative(project.updatedAt)}`}
                  </p>
                  {(project.memoryItems?.length ?? 0) > 0 && (
                    <p className="mt-2 text-[12px] text-[#3a3a3a]">
                      {project.memoryItems!.length} memor
                      {project.memoryItems!.length === 1 ? 'y' : 'ies'}
                    </p>
                  )}
                  {project.contextItems.length > 0 && (
                    <p className="mt-1 text-[12px] text-[#3a3a3a]">
                      {project.contextItems.length} context item
                      {project.contextItems.length === 1 ? '' : 's'}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Right rail */}
          <aside className="lg:sticky lg:top-4 rounded-2xl border border-[#1c1c1c] bg-[#0f0f0f] overflow-hidden shadow-sm">
            <InstructionsPanel project={project} />
            <MemoryPanel project={project} />
            <ContextPanel project={project} onOpenNostrPicker={onOpenNostrPicker} />
          </aside>
        </div>
      </div>
    </div>
  );
}

function formatRelative(ts: number): string {
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d`;
  try {
    return new Date(ts).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}
